import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Globe, Plus, Trash2, Play, Upload, Zap, X, ChevronRight, Gauge, FileText, Sparkles, Loader2 } from "lucide-react";
import PlaybookTab from "./PlaybookTab";
import GeoTab from "./GeoTab";
import OverviewTab from "./OverviewTab";
import TrafficTab from "./TrafficTab";

/* ---------- Tabs ---------- */
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "traffic", label: "Traffic" },
  { key: "playbook", label: "Playbook" },
  { key: "properties", label: "Properties" },
  { key: "audit", label: "On-page Audit" },
  { key: "bulk", label: "Bulk Audit" },
  { key: "pagespeed", label: "PageSpeed" },
  { key: "gsc", label: "Search Console Import" },
  { key: "geo", label: "GEO Readiness" },
];

/* ---------- Small UI helpers ---------- */
function StatChip({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-beige text-ink",
    good: "bg-forest text-cream",
    warn: "bg-pollen text-ink",
    bad: "bg-red-500/90 text-white",
  };
  return (
    <div className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest ${tones[tone]}`}>
      <span className="opacity-70">{label}: </span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
function scoreTone(s) {
  if (s == null) return "default";
  if (s >= 80) return "good";
  if (s >= 50) return "warn";
  return "bad";
}

/* ---------- AI Fix Plan ---------- */
const PRIORITY_STYLES = {
  critical: "bg-red-600 text-white",
  high: "bg-red-400 text-white",
  medium: "bg-pollen text-ink",
  low: "bg-beige text-ink border border-forest/15",
};

function FixPlan({ audit, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const plan = audit.ai_suggestions;

  const generate = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/seo/audits/${audit.id}/suggestions`);
      toast.success("AI fix plan ready");
      setOpen(true);
      onRefresh();
    } catch { toast.error("Fix plan failed — try again"); }
    setLoading(false);
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid={`seo-fixplan-btn-${audit.id}`}
          onClick={generate}
          disabled={loading}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-forest text-cream hover:bg-forestDeep disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Writing fix plan…" : plan ? "Regenerate AI Fix Plan" : "AI Fix Plan"}
        </button>
        {plan && (
          <button onClick={() => setOpen(!open)} className="text-xs uppercase tracking-widest text-forest link-underline">
            {open ? "Hide plan" : `Show plan (${plan.tasks?.length || 0} tasks)`}
          </button>
        )}
      </div>
      {plan && open && (
        <div data-testid={`seo-fixplan-${audit.id}`} className="mt-3 rounded-lg bg-cream border border-forest/10 p-4 space-y-3">
          {plan.summary && <p className="text-sm text-ink">{plan.summary}</p>}
          {(plan.proposed_title || plan.proposed_meta_description) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {plan.proposed_title && (
                <div className="rounded-lg bg-beige border border-forest/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Proposed title</div>
                  <div className="text-sm text-ink">{plan.proposed_title}</div>
                </div>
              )}
              {plan.proposed_meta_description && (
                <div className="rounded-lg bg-beige border border-forest/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Proposed meta description</div>
                  <div className="text-sm text-ink">{plan.proposed_meta_description}</div>
                </div>
              )}
            </div>
          )}
          <ol className="space-y-2.5">
            {(plan.tasks || []).map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full mt-0.5 shrink-0 ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.low}`}>{t.priority}</span>
                <div className="min-w-0">
                  <div className="text-sm text-ink font-medium">{t.task}</div>
                  {t.how && <div className="text-xs text-inkmute mt-0.5">{t.how}</div>}
                  {t.category && <div className="text-[9px] uppercase tracking-widest text-clay mt-0.5">{t.category}</div>}
                </div>
              </li>
            ))}
          </ol>
          <div className="text-[10px] text-inkmute">Generated {new Date(plan.generated_at).toLocaleString()} · {plan.model}</div>
        </div>
      )}
    </div>
  );
}

/* ---------- Properties tab ---------- */
function PropertiesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = {
    name: "",
    site_url: "",
    sitemap_url: "",
    brand_name: "",
    brand_aliases: "",
    socials_instagram: "",
    socials_linkedin: "",
    socials_facebook: "",
    socials_youtube: "",
    competitors: "",
    is_primary: false,
    notes: "",
  };
  const [form, setForm] = useState(empty);

  const load = () =>
    api.get("/admin/seo/properties").then((r) => setItems(r.data)).catch(() => toast.error("Load failed"));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "",
      site_url: p.site_url || "",
      sitemap_url: p.sitemap_url || "",
      brand_name: p.brand_name || "",
      brand_aliases: (p.brand_aliases || []).join(", "),
      socials_instagram: p.socials?.instagram || "",
      socials_linkedin: p.socials?.linkedin || "",
      socials_facebook: p.socials?.facebook || "",
      socials_youtube: p.socials?.youtube || "",
      competitors: (p.competitors || []).join(", "),
      is_primary: !!p.is_primary,
      notes: p.notes || "",
    });
  };

  const save = async () => {
    const payload = {
      name: form.name,
      site_url: form.site_url,
      sitemap_url: form.sitemap_url || null,
      brand_name: form.brand_name || "Pollen Dots",
      brand_aliases: form.brand_aliases.split(",").map((s) => s.trim()).filter(Boolean),
      socials: {
        instagram: form.socials_instagram, linkedin: form.socials_linkedin,
        facebook: form.socials_facebook, youtube: form.socials_youtube,
      },
      competitors: form.competitors.split(",").map((s) => s.trim()).filter(Boolean),
      is_primary: !!form.is_primary,
      notes: form.notes || null,
    };
    try {
      if (editing === "new") await api.post("/admin/seo/properties", payload);
      else await api.patch(`/admin/seo/properties/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/admin/seo/properties/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-inkmute">Register websites, brand aliases, socials and competitors.</div>
        <button onClick={openNew} data-testid="seo-new-property" className="inline-flex items-center gap-2 bg-forest text-cream text-xs uppercase tracking-widest px-4 py-2 rounded-full hover:bg-forestDeep">
          <Plus className="w-4 h-4" /> New Property
        </button>
      </div>

      {editing && (
        <div className="mb-6 rounded-xl border border-forest/15 bg-beige p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-serif text-2xl text-ink">{editing === "new" ? "New Property" : "Edit Property"}</div>
            <button onClick={() => setEditing(null)} className="text-inkmute hover:text-forest"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["name", "Property Name", "Pollen Dots (Primary)"],
              ["site_url", "Site URL", "https://pollendots.com"],
              ["sitemap_url", "Sitemap URL", "https://pollendots.com/sitemap.xml"],
              ["brand_name", "Brand Name", "Pollen Dots"],
              ["brand_aliases", "Brand Aliases (comma-separated)", "Pollen Dots, pollendots, pollendots.com"],
              ["competitors", "Competitor Domains (comma-separated)", "someagency.com, othertravel.in"],
              ["socials_instagram", "Instagram URL", "https://instagram.com/..."],
              ["socials_linkedin", "LinkedIn URL", "https://linkedin.com/..."],
              ["socials_facebook", "Facebook URL", "https://facebook.com/..."],
              ["socials_youtube", "YouTube URL", "https://youtube.com/..."],
              ["notes", "Internal Notes", ""],
            ].map(([k, label, ph]) => (
              <label key={k} className={`block ${k === "notes" ? "md:col-span-2" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest text-clay mb-1">{label}</div>
                <input
                  data-testid={`seo-prop-${k}`}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={ph}
                  className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-forest/40"
                />
              </label>
            ))}
            <label className="flex items-center gap-2 md:col-span-2">
              <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
              <span className="text-sm text-ink">Primary property (used by scheduled AEO sweeps)</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-widest px-4 py-2 border border-forest/20 rounded-full">Cancel</button>
            <button onClick={save} data-testid="seo-prop-save" className="text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-forest text-cream hover:bg-forestDeep">Save</button>
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-forest/10 bg-beige">
        <table className="w-full text-sm">
          <thead className="bg-cream border-b border-forest/10 text-[10px] uppercase tracking-[0.2em] text-clay">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Site</th>
              <th className="text-left p-4 hidden md:table-cell">Brand aliases</th>
              <th className="text-left p-4 hidden lg:table-cell">Competitors</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-inkmute">No properties yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} data-testid={`seo-prop-row-${p.id}`} className="border-b border-forest/10 hover:bg-cream/60">
                <td className="p-4">
                  <div className="font-medium text-ink flex items-center gap-2">
                    {p.name}
                    {p.is_primary && <span className="text-[9px] px-2 py-0.5 rounded-full bg-pollen text-ink uppercase tracking-widest">Primary</span>}
                  </div>
                </td>
                <td className="p-4 text-inkmute text-xs">{p.site_url}</td>
                <td className="p-4 hidden md:table-cell text-xs text-inkmute">{(p.brand_aliases || []).join(", ")}</td>
                <td className="p-4 hidden lg:table-cell text-xs text-inkmute">{(p.competitors || []).join(", ") || "—"}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(p)} className="text-xs uppercase tracking-widest text-forest mr-4 link-underline">Edit</button>
                  <button onClick={() => remove(p.id)} className="text-inkmute hover:text-forest"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Audit tab ---------- */
function AuditTab() {
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [audits, setAudits] = useState([]);
  const load = () => api.get("/admin/seo/audits").then((r) => setAudits(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!url) return toast.error("Enter a URL");
    setRunning(true);
    try {
      await api.post("/admin/seo/audit", { url });
      toast.success("Audit complete");
      setUrl(""); load();
    } catch { toast.error("Audit failed"); }
    setRunning(false);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this audit?")) return;
    await api.delete(`/admin/seo/audits/${id}`); load();
  };

  return (
    <div>
      <div className="rounded-xl bg-beige border border-forest/10 p-4 mb-6 flex gap-3">
        <input
          data-testid="seo-audit-url"
          value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://pollendots.com/insights/why-slow-travel"
          className="flex-1 bg-cream border border-forest/15 rounded-lg px-4 py-2 text-sm"
        />
        <button data-testid="seo-audit-run" onClick={run} disabled={running} className="inline-flex items-center gap-2 bg-forest text-cream px-5 py-2 rounded-full text-xs uppercase tracking-widest hover:bg-forestDeep disabled:opacity-50">
          <Zap className="w-4 h-4" /> {running ? "Auditing…" : "Audit URL"}
        </button>
      </div>

      <div className="space-y-4">
        {audits.length === 0 && <div className="text-inkmute text-sm">No audits yet — paste a URL above and press Audit.</div>}
        {audits.map((a) => (
          <div key={a.id} data-testid={`seo-audit-row-${a.id}`} className="rounded-xl border border-forest/10 bg-beige p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="text-ink font-medium truncate">{a.url}</div>
                <div className="text-xs text-inkmute mt-1">{new Date(a.audited_at || a.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatChip label="Score" value={a.score} tone={scoreTone(a.score)} />
                <button onClick={() => remove(a.id)} className="text-inkmute hover:text-forest"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <StatChip label="Title" value={`${a.title_length}c`} />
              <StatChip label="Meta" value={`${a.meta_description_length}c`} />
              <StatChip label="H1" value={a.h1_count} />
              <StatChip label="H2" value={a.h2_count} />
              <StatChip label="Words" value={a.word_count} />
              <StatChip label="Images" value={`${a.image_count} · ${a.images_missing_alt} no-alt`} />
              <StatChip label="Internal" value={a.internal_links} />
              <StatChip label="External" value={a.external_links} />
              <StatChip label="Schema" value={a.schema_types?.length || 0} />
              <StatChip label="Load" value={`${a.load_time_ms || 0}ms`} />
            </div>
            {a.issues && a.issues.length > 0 && (
              <div className="rounded-lg bg-cream border border-forest/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-clay mb-2">Issues ({a.issues.length})</div>
                <ul className="space-y-1 text-sm">
                  {a.issues.map((i, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full mt-0.5 ${
                        i.severity === "critical" ? "bg-red-600 text-white" :
                        i.severity === "high" ? "bg-red-400 text-white" :
                        i.severity === "medium" ? "bg-pollen text-ink" :
                        "bg-beige text-ink"
                      }`}>{i.severity}</span>
                      <span className="text-ink">{i.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <FixPlan audit={a} onRefresh={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- PageSpeed tab ---------- */
function SpeedPlan({ p, onDone }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const plan = p.ai_fix_plan;
  const generate = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/seo/pagespeed/${p.id}/fix-plan`);
      toast.success("Speed fix plan ready");
      setOpen(true);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Speed plan failed"); }
    setLoading(false);
  };
  if (p.status !== "done") return null;
  return (
    <div className="mt-4">
      <div className="flex items-center gap-3">
        <button data-testid={`ps-fixplan-btn-${p.id}`} onClick={generate} disabled={loading}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-forest text-cream hover:bg-forestDeep disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Writing…" : plan ? "Regenerate Speed Fix Plan" : "AI Speed Fix Plan"}
        </button>
        {plan && <button onClick={() => setOpen(!open)} className="text-xs uppercase tracking-widest text-forest link-underline">{open ? "Hide" : `Show (${plan.tasks?.length || 0} tasks)`}</button>}
      </div>
      {plan && open && (
        <div data-testid={`ps-fixplan-${p.id}`} className="mt-3 rounded-lg bg-cream border border-forest/10 p-4 space-y-3">
          {plan.summary && <p className="text-sm text-ink">{plan.summary}</p>}
          <ol className="space-y-2.5">
            {(plan.tasks || []).map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full mt-0.5 shrink-0 ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.low}`}>{t.priority}</span>
                <div className="min-w-0">
                  <div className="text-sm text-ink font-medium">{t.task}</div>
                  {t.how && <div className="text-xs text-inkmute mt-0.5">{t.how}</div>}
                  {t.expected_impact && <div className="text-[10px] uppercase tracking-widest text-clay mt-0.5">{t.expected_impact}</div>}
                </div>
              </li>
            ))}
          </ol>
          <div className="text-[10px] text-inkmute">Generated {new Date(plan.generated_at).toLocaleString()} · {plan.model}</div>
        </div>
      )}
    </div>
  );
}

function PageSpeedTab() {
  const [url, setUrl] = useState("");
  const [strategy, setStrategy] = useState("mobile");
  const [running, setRunning] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/seo/pagespeed").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!url) return toast.error("Enter a URL");
    setRunning(true);
    try {
      await api.post("/admin/seo/pagespeed", { url, strategy });
      toast.success("PageSpeed started — Google takes about a minute, the result appears below");
      setUrl(""); load();
    } catch { toast.error("PageSpeed failed to start"); }
    setRunning(false);
  };

  useEffect(() => {
    if (!items.some((p) => p.status === "running")) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [items]);

  return (
    <div>
      <div className="rounded-xl bg-beige border border-forest/10 p-4 mb-6 flex flex-wrap gap-3 items-center">
        <input
          data-testid="ps-url"
          value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://pollendots.com/"
          className="flex-1 min-w-[240px] bg-cream border border-forest/15 rounded-lg px-4 py-2 text-sm"
        />
        <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className="bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm">
          <option value="mobile">Mobile</option>
          <option value="desktop">Desktop</option>
        </select>
        <button data-testid="ps-run" onClick={run} disabled={running} className="inline-flex items-center gap-2 bg-forest text-cream px-5 py-2 rounded-full text-xs uppercase tracking-widest hover:bg-forestDeep disabled:opacity-50">
          <Gauge className="w-4 h-4" /> {running ? "Running…" : "Run PageSpeed"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && <div className="text-inkmute text-sm">No PageSpeed results yet.</div>}
        {items.map((p) => (
          <div key={p.id} className="rounded-xl border border-forest/10 bg-beige p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                <div className="font-medium text-ink truncate">{p.url}</div>
                <div className="text-xs text-inkmute uppercase tracking-widest mt-1">{p.strategy}</div>
              </div>
              {p.status === "running" && <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-pollen text-ink shrink-0"><Loader2 className="w-3 h-3 animate-spin" /> running</span>}
              {p.status === "failed" && <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-500 text-white shrink-0">failed</span>}
            </div>
            {p.status === "failed" && p.error && <div className="text-xs text-red-600 mb-3">{p.error}</div>}
            <div className="grid grid-cols-4 gap-2">
              {["performance_score", "accessibility_score", "best_practices_score", "seo_score"].map((k) => (
                <div key={k} className="text-center">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full text-lg font-serif ${
                    p[k] == null ? "bg-beige text-inkmute" :
                    p[k] >= 90 ? "bg-forest text-cream" :
                    p[k] >= 50 ? "bg-pollen text-ink" :
                    "bg-red-500 text-white"
                  }`}>{p[k] ?? "—"}</div>
                  <div className="text-[9px] uppercase tracking-widest text-clay mt-1">{k.replace(/_score/, "").replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-[10px] uppercase tracking-widest text-clay">
              <div>LCP<br /><span className="text-ink font-serif text-sm normal-case tracking-normal">{p.lcp_ms ? `${(p.lcp_ms/1000).toFixed(1)}s` : "—"}</span></div>
              <div>CLS<br /><span className="text-ink font-serif text-sm normal-case tracking-normal">{p.cls != null ? p.cls.toFixed(3) : "—"}</span></div>
              <div>TBT<br /><span className="text-ink font-serif text-sm normal-case tracking-normal">{p.tbt_ms ? `${p.tbt_ms}ms` : "—"}</span></div>
            </div>
            <SpeedPlan p={p} onDone={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- GSC Import tab ---------- */
function GscTab() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imports, setImports] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/admin/seo/gsc/imports").then((r) => setImports(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setFile(f);
    e.target.value = "";
  };
  const upload = async () => {
    if (!file) return toast.error("Choose a CSV or ZIP first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/seo/gsc/upload", fd);
      const list = r.data.imported;
      toast.success(`Imported ${list.length} file${list.length > 1 ? "s" : ""}: ${list.map((x) => `${x.filename} (${x.row_count})`).join(", ")}`);
      setFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setUploading(false);
  };
  const openImport = async (id) => {
    const r = await api.get(`/admin/seo/gsc/imports/${id}`);
    setSelected(r.data);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this import?")) return;
    await api.delete(`/admin/seo/gsc/imports/${id}`); load(); if (selected?.id === id) setSelected(null);
  };

  return (
    <div>
      <div className="rounded-xl bg-beige border border-forest/10 p-5 mb-6">
        <div className="text-sm text-ink mb-2 font-medium">How to export from Google Search Console</div>
        <ol className="text-sm text-inkmute list-decimal ml-5 space-y-1 mb-4">
          <li>Open <a className="link-underline" href="https://search.google.com/search-console" target="_blank" rel="noreferrer">search.google.com/search-console</a></li>
          <li>Go to <b>Performance</b> → set your date range</li>
          <li>Click <b>Export</b> → <b>Download CSV</b> — you get a <b>.zip</b> with Queries, Pages, Countries and more</li>
          <li>Upload the whole ZIP here (or a single CSV) — every table inside is parsed and stored</li>
        </ol>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer text-xs uppercase tracking-widest text-forest border border-forest/25 rounded-full px-4 py-2 hover:bg-forest hover:text-cream transition-colors">
            <Upload className="w-4 h-4" /> Choose CSV or ZIP
            <input data-testid="gsc-file" type="file" accept=".csv,.zip,text/csv,application/zip" className="hidden" onChange={onFile} />
          </label>
          {file && <span className="text-xs text-inkmute">{file.name}</span>}
          <button data-testid="gsc-upload" onClick={upload} disabled={!file || uploading} className="inline-flex items-center gap-2 bg-forest text-cream text-xs uppercase tracking-widest px-4 py-2 rounded-full disabled:opacity-40">
            {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {uploading ? "Importing…" : "Upload"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        <div className="rounded-xl border border-forest/10 bg-beige overflow-hidden">
          <div className="p-4 text-[10px] uppercase tracking-widest text-clay border-b border-forest/10">Recent imports</div>
          {imports.length === 0 && <div className="p-6 text-sm text-inkmute">No imports yet.</div>}
          {imports.map((i) => (
            <div key={i.id} className={`p-4 border-b border-forest/10 cursor-pointer hover:bg-cream ${selected?.id === i.id ? "bg-cream" : ""}`} onClick={() => openImport(i.id)}>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-ink truncate">{i.filename}</div>
                  <div className="text-xs text-inkmute">{i.source} · {i.row_count} rows</div>
                  <div className="text-[10px] text-inkmute mt-1">{new Date(i.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-inkmute" />
                  <button onClick={(e) => { e.stopPropagation(); remove(i.id); }} className="text-inkmute hover:text-forest"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-forest/10 bg-beige p-5 min-h-[300px]">
          {!selected ? (
            <div className="text-inkmute text-sm">Select an import on the left to see the parsed rows.</div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-serif text-xl text-ink">{selected.filename}</div>
                  <div className="text-xs text-inkmute">{selected.source} · {selected.row_count} rows</div>
                </div>
              </div>
              <div className="overflow-auto max-h-[520px] rounded-lg border border-forest/10">
                <table className="w-full text-xs">
                  <thead className="bg-cream text-[10px] uppercase tracking-widest text-clay sticky top-0">
                    <tr>
                      {Object.keys(selected.rows[0] || {}).map((k) => <th key={k} className="text-left p-2">{k}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.rows || []).slice(0, 500).map((r, idx) => (
                      <tr key={idx} className="border-t border-forest/5">
                        {Object.keys(selected.rows[0] || {}).map((k) => <td key={k} className="p-2 text-ink">{String(r[k] ?? "")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sitemap Bulk Audit tab ---------- */
function BulkTab() {
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [limit, setLimit] = useState(50);
  const [starting, setStarting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [planRow, setPlanRow] = useState(null);
  const openRef = useRef(null);
  openRef.current = open;

  const startFixPlans = async (id) => {
    try {
      const r = await api.post(`/admin/seo/bulk-audit/${id}/fix-plans`, { threshold: 70 });
      if (r.data.queued === 0) toast.info(r.data.message);
      else toast.success(`Writing ${r.data.queued} fix plans in the background…`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  useEffect(() => {
    api.get("/admin/seo/properties").then((r) => {
      const p = r.data.find((x) => x.is_primary) || r.data[0];
      if (p?.sitemap_url) setSitemapUrl((prev) => prev || p.sitemap_url);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = async () => {
      try {
        const r = await api.get("/admin/seo/bulk-audit");
        setJobs(r.data);
        if (openRef.current) {
          const d = await api.get(`/admin/seo/bulk-audit/${openRef.current}`);
          setDetail(d.data);
        }
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    if (!sitemapUrl) return toast.error("Enter a sitemap URL");
    setStarting(true);
    try {
      const r = await api.post("/admin/seo/bulk-audit", { sitemap_url: sitemapUrl, limit: Number(limit) || 50 });
      toast.success(`Bulk audit started — ${r.data.total_urls} pages queued`);
      setJobs((prev) => [r.data, ...prev]);
      setOpen(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start bulk audit"); }
    setStarting(false);
  };

  const openJob = async (id) => {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id); setDetail(null);
    try {
      const r = await api.get(`/admin/seo/bulk-audit/${id}`);
      setDetail(r.data);
    } catch { /* ignore */ }
  };

  const removeJob = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this bulk audit and all its page results?")) return;
    await api.delete(`/admin/seo/bulk-audit/${id}`);
    if (open === id) { setOpen(null); setDetail(null); }
    setJobs((prev) => prev.filter((j) => j.id !== id));
  };

  return (
    <div>
      <div className="rounded-xl bg-beige border border-forest/10 p-4 mb-6 flex flex-wrap gap-3 items-center">
        <input
          data-testid="bulk-sitemap-url"
          value={sitemapUrl} onChange={(e) => setSitemapUrl(e.target.value)}
          placeholder="https://pollendots.com/sitemap.xml"
          className="flex-1 min-w-[240px] bg-cream border border-forest/15 rounded-lg px-4 py-2 text-sm"
        />
        <label className="flex items-center gap-2 text-xs text-inkmute">
          Max pages
          <input data-testid="bulk-limit" type="number" min={1} max={5000} value={limit} onChange={(e) => setLimit(e.target.value)}
            className="w-24 bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm text-ink" />
        </label>
        <button data-testid="bulk-start" onClick={start} disabled={starting}
          className="inline-flex items-center gap-2 bg-forest text-cream px-5 py-2 rounded-full text-xs uppercase tracking-widest hover:bg-forestDeep disabled:opacity-50">
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {starting ? "Crawling…" : "Audit Entire Sitemap"}
        </button>
      </div>

      <div className="space-y-3">
        {jobs.length === 0 && <div className="text-inkmute text-sm">No bulk audits yet — point us at a sitemap and every page gets scored in one go. Weakest pages float to the top.</div>}
        {jobs.map((j) => (
          <div key={j.id} className="rounded-xl border border-forest/10 bg-beige overflow-hidden">
            <div data-testid={`bulk-job-row-${j.id}`} role="button" tabIndex={0} onClick={() => openJob(j.id)} className="w-full flex items-center justify-between p-4 hover:bg-cream text-left gap-3 cursor-pointer">
              <div className="min-w-0 flex-1">
                <div className="text-ink font-medium truncate">{j.sitemap_url}</div>
                <div className="text-xs text-inkmute mt-1 flex flex-wrap items-center gap-2">
                  <span className={`inline-block text-[9px] px-2 py-0.5 rounded-full uppercase tracking-widest ${
                    j.status === "done" ? "bg-forest text-cream" : j.status === "running" ? "bg-pollen text-ink" : j.status === "failed" ? "bg-red-500 text-white" : "bg-cream text-inkmute"
                  }`}>{j.status}</span>
                  <span>{new Date(j.created_at).toLocaleString()}</span>
                  <span>· {j.completed}/{j.total_urls} pages</span>
                  {j.status === "done" && <span>· avg score {j.avg_score}</span>}
                  {j.status === "done" && (j.weak_count || 0) > 0 && <span className="text-red-600 font-medium">· {j.weak_count} weak pages</span>}
                  {(j.failed_count || 0) > 0 && <span>· {j.failed_count} failed</span>}
                </div>
                {j.status === "running" && (
                  <div className="mt-2 h-1.5 rounded-full bg-cream overflow-hidden">
                    <div className="h-full bg-forest transition-all" style={{ width: `${Math.round((j.completed / Math.max(1, j.total_urls)) * 100)}%` }} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={(e) => removeJob(e, j.id)} className="text-inkmute hover:text-forest"><Trash2 className="w-4 h-4" /></button>
                <ChevronRight className={`w-4 h-4 text-inkmute transition-transform ${open === j.id ? "rotate-90" : ""}`} />
              </div>
            </div>
            {open === j.id && (
              <div data-testid={`bulk-results-${j.id}`} className="border-t border-forest/10 bg-cream max-h-[560px] overflow-auto">
                {j.status === "done" && (
                  <div className="p-3 border-b border-forest/10 flex flex-wrap items-center gap-3">
                    {j.fixplans?.running ? (
                      <span className="inline-flex items-center gap-1.5 text-xs bg-pollen text-ink px-3 py-1.5 rounded-full"><Loader2 className="w-3.5 h-3.5 animate-spin" /> writing fix plans {j.fixplans.done}/{j.fixplans.total}…</span>
                    ) : (
                      <button data-testid={`bulk-fixplans-${j.id}`} onClick={() => startFixPlans(j.id)}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-forest text-cream hover:bg-forestDeep">
                        <Sparkles className="w-3.5 h-3.5" /> AI fix plans for weak pages (&lt;70)
                      </button>
                    )}
                    <span className="text-[11px] text-inkmute">Rows with a plan show a "plan" chip — click it to read.</span>
                  </div>
                )}
                {!detail || detail.job.id !== j.id ? (
                  <div className="p-4 text-inkmute text-sm">Loading…</div>
                ) : detail.results.length === 0 ? (
                  <div className="p-4 text-inkmute text-sm">{j.status === "running" || j.status === "queued" ? "Auditing — results appear as pages finish." : "No results."}</div>
                ) : (
                  <>
                  {detail.results_total > detail.results.length && (
                    <div className="px-3 py-2 text-[11px] text-inkmute bg-beige border-b border-forest/10">Showing the weakest {detail.results.length} of {detail.results_total} pages.</div>
                  )}
                  <table className="w-full text-sm">
                    <thead className="bg-beige text-[10px] uppercase tracking-widest text-clay sticky top-0">
                      <tr>
                        <th className="text-left p-3">Score</th>
                        <th className="text-left p-3">Page</th>
                        <th className="text-left p-3 hidden md:table-cell">Title / Meta</th>
                        <th className="text-left p-3 hidden lg:table-cell">Words</th>
                        <th className="text-left p-3">Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.results.map((r) => (
                        <React.Fragment key={r.id}>
                        <tr className={`border-t border-forest/10 ${r.score < 50 ? "bg-red-50" : ""}`}>
                          <td className="p-3"><span className="inline-flex"><StatChip label="" value={r.score} tone={scoreTone(r.score)} /></span></td>
                          <td className="p-3 max-w-[320px]">
                            <a href={r.url} target="_blank" rel="noreferrer" className="text-ink hover:text-forest text-xs break-all link-underline">{r.url}</a>
                          </td>
                          <td className="p-3 hidden md:table-cell text-xs text-inkmute">{r.title_length}c / {r.meta_description_length}c</td>
                          <td className="p-3 hidden lg:table-cell text-xs text-inkmute">{r.word_count}</td>
                          <td className="p-3 text-xs">
                            <span className="inline-flex items-center gap-1.5">
                            {(r.issues?.length || 0) > 0 ? (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${r.issues.some((i) => i.severity === "critical" || i.severity === "high") ? "bg-red-400 text-white" : "bg-pollen text-ink"}`}>
                                {r.issues.length}
                              </span>
                            ) : <span className="text-inkmute">—</span>}
                            {r.ai_suggestions && (
                              <button onClick={() => setPlanRow(planRow === r.id ? null : r.id)} className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-forest text-cream hover:bg-forestDeep">plan</button>
                            )}
                            </span>
                          </td>
                        </tr>
                        {planRow === r.id && r.ai_suggestions && (
                          <tr className="border-t border-forest/10">
                            <td colSpan={5} className="p-4 bg-beige">
                              {r.ai_suggestions.summary && <p className="text-sm text-ink mb-2">{r.ai_suggestions.summary}</p>}
                              <ol className="space-y-1.5">
                                {(r.ai_suggestions.tasks || []).map((t, i) => (
                                  <li key={i} className="text-xs text-ink"><span className="uppercase tracking-widest text-[9px] text-clay mr-1.5">{t.priority}</span>{t.task}{t.how ? <span className="text-inkmute"> — {t.how}</span> : null}</li>
                                ))}
                              </ol>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Main page ---------- */
export default function SEODashboard() {
  const [tab, setTab] = useState("overview");
  useEffect(() => { document.title = "Growth Engine · Pollen Dots CMS"; }, []);

  return (
    <Layout>
    <div data-testid="seo-dashboard">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.24em] text-clay">CMS · CONTENT, SEARCH & AI</div>
        <h1 className="font-serif text-5xl text-ink mt-1">Growth Engine</h1>
        <p className="text-sm text-inkmute mt-2 max-w-2xl">
          The backbone of the content, SEO, AEO, GEO and keyword strategy — the playbook, page audits,
          AI visibility and crawler readiness, all in one place. Everything here is free — no subscriptions.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-forest/10 -mx-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`seo-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors border-b-2 -mb-px ${
              tab === t.key ? "border-forest text-forest" : "border-transparent text-inkmute hover:text-ink"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab goTab={setTab} />}
      {tab === "traffic" && <TrafficTab />}
      {tab === "playbook" && <PlaybookTab />}
      {tab === "properties" && <PropertiesTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "bulk" && <BulkTab />}
      {tab === "pagespeed" && <PageSpeedTab />}
      {tab === "gsc" && <GscTab />}
      {tab === "geo" && <GeoTab />}
    </div>
    </Layout>
  );
}
